/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
    TextareaHTMLAttributes,
    useEffect,
    useRef,
    useCallback,
} from "react";
import { IconBaseProps } from "react-icons";
import { useField } from "@unform/core";

import ReactInputMask, { Props as MaskProps } from "react-input-mask";

import { Container, MessageError, LabelContainer } from "./styles";

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
    name: string;
    label?: string;
    icon?: React.ComponentType<IconBaseProps>;
}

export const TextArea: React.FC<TextAreaProps> = ({
    name,
    icon: Icon,
    label,
    ...rest
}) => {
    const textareaRef = useRef(null);

    const { fieldName, registerField, error, clearError } = useField(name);

    const onFocus = useCallback(() => {
        clearError();
    }, [clearError]);

    useEffect(() => {
        registerField({
            name: fieldName,
            ref: textareaRef.current,
            path: "value",
        });
    }, [fieldName, registerField]);

    return (
        <Container
            style={{
                display: `${rest.hidden ? "none" : "flex"}`,
            }}
        >
            {(label || error) && (
                <LabelContainer>
                    {error ? (
                        <MessageError>{error}</MessageError>
                    ) : (
                        <p className="text-sm text-teal-800">{label}</p>
                    )}
                </LabelContainer>
            )}
            {Icon && <Icon size={20} color={error && "red"} />}

            <textarea ref={textareaRef} {...rest} onFocus={onFocus} rows={4} />
        </Container>
    );
};

export interface MaskTextAreaProps extends MaskProps {
    name: string;
    label?: string;
    icon?: React.ComponentType<IconBaseProps>;
}

export const InputMask: React.FC<MaskTextAreaProps> = ({
    name,
    icon: Icon,
    label,
    ...rest
}) => {
    const textareaRef = useRef(null);
    const { fieldName, registerField, defaultValue, clearError, error } =
        useField(name);

    const onFocus = useCallback(() => {
        clearError();
    }, [clearError]);

    useEffect(() => {
        registerField({
            name: fieldName,
            ref: textareaRef.current,
            path: "value",
            setValue(ref: any, value: string) {
                ref.setTextAreaValue(value);
            },
            clearValue(ref: any) {
                ref.setTextAreaValue("");
            },
        });
    }, [fieldName, registerField]);

    return (
        <Container
            style={{
                display: `${rest.hidden ? "none" : null}`,
            }}
        >
            {(label || error) && (
                <LabelContainer>
                    {error ? (
                        <MessageError>{error}</MessageError>
                    ) : (
                        <p>{label}</p>
                    )}
                </LabelContainer>
            )}
            {Icon && <Icon size={20} color={error && "red"} />}

            <ReactInputMask
                ref={textareaRef}
                defaultValue={defaultValue}
                onFocus={onFocus}
                {...rest}
            />
        </Container>
    );
};
